/**
 * AwinIntegrationApiKeyPublisherIdPage — connection form + sync-status panel
 * + recent-sync feed for the Partner Network (Awin) credential store.
 * Light theme, GREEN accent only.
 *
 * Polymorphic copy rule: the exact partner name "Awin" appears once, in the
 * page title. Everywhere else uses the neutral label "Partner Network".
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	isValidApiKey,
	isValidPublisherId,
	type CredentialRow,
} from "./api";
import {
	useAwinIntegrationApiKeyPublisherIdCreate,
	useAwinIntegrationApiKeyPublisherIdDelete,
	useAwinIntegrationApiKeyPublisherIdList,
} from "./hooks";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const INPUT_INVALID =
	"w-full rounded-[6px] border border-red-400 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

function formatTimestamp(epochSec: number): string {
	if (!epochSec || Number.isNaN(epochSec)) return "never";
	const d = new Date(epochSec * 1000);
	if (Number.isNaN(d.getTime())) return "never";
	return d.toISOString().replace("T", " ").slice(0, 19) + " UTC";
}

function lastSyncFromCreds(items: CredentialRow[] | undefined): number {
	if (!items || items.length === 0) return 0;
	return items.reduce(
		(max, r) => (r.updated_at > max ? r.updated_at : max),
		0,
	);
}

function activeCount(items: CredentialRow[] | undefined): number {
	if (!items || items.length === 0) return 0;
	return items.filter((r) => r.status === "active").length;
}

export function AwinIntegrationApiKeyPublisherIdPage() {
	const credsQ = useAwinIntegrationApiKeyPublisherIdList();
	const createMut = useAwinIntegrationApiKeyPublisherIdCreate();
	const deleteMut = useAwinIntegrationApiKeyPublisherIdDelete();

	const [publisherId, setPublisherId] = useState("");
	const [apiKey, setApiKey] = useState("");
	const [label, setLabel] = useState("");
	const [saved, setSaved] = useState(false);

	const publisherIdValid =
		publisherId.length === 0 || isValidPublisherId(publisherId);
	const apiKeyValid = apiKey.length === 0 || isValidApiKey(apiKey);
	const canSubmit =
		publisherId.length > 0 &&
		apiKey.length > 0 &&
		publisherIdValid &&
		apiKeyValid &&
		!createMut.isPending;

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		setSaved(false);
		if (!canSubmit) return;
		createMut.mutate(
			{
				publisher_id: publisherId.trim(),
				api_key: apiKey.trim(),
				label: label.trim() || undefined,
			},
			{
				onSuccess: () => {
					setSaved(true);
					setApiKey("");
				},
			},
		);
	};

	const items = credsQ.data?.items ?? [];
	const lastSyncEpoch = useMemo(() => lastSyncFromCreds(items), [items]);
	const activeCreds = useMemo(() => activeCount(items), [items]);
	const connected = activeCreds > 0;

	return (
		<main
			data-testid="awin-integration-api-key-publisher-id-page"
			className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<h1 className="bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-3xl font-semibold text-transparent">
					Awin Integration
				</h1>
				<p className="text-sm text-zinc-600">
					Partner Network Integration. Connect your Partner Network account to
					ingest conversion postbacks and map offers to Trackerity.
				</p>
			</header>

			{/* Sync status panel */}
			<section
				className={CARD}
				data-testid="awin-integration-api-key-publisher-id-status"
				aria-label="sync status"
			>
				<div className="flex flex-col gap-3">
					<div className="flex items-center justify-between">
						<h2 className="text-sm font-semibold text-zinc-800">
							Partner Network Status
						</h2>
						<span
							className={
								connected
									? "rounded-full border border-green-300 bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-800"
									: "rounded-full border border-zinc-300 bg-zinc-50 px-2.5 py-0.5 text-xs font-medium text-zinc-700"
							}
							data-testid="awin-integration-api-key-publisher-id-status-badge"
						>
							{credsQ.isLoading
								? "checking"
								: connected
									? "Connected"
									: "Not connected"}
						</span>
					</div>
					<dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
						<div>
							<dt className="text-xs uppercase tracking-wide text-zinc-500">
								Active Credentials
							</dt>
							<dd
								className="text-sm text-zinc-900"
								data-testid="awin-integration-api-key-publisher-id-status-creds"
							>
								{activeCreds > 0 ? `${activeCreds} configured` : "Missing"}
							</dd>
						</div>
						<div>
							<dt className="text-xs uppercase tracking-wide text-zinc-500">
								Publishers
							</dt>
							<dd className="text-sm text-zinc-900">
								{items.length > 0 ? String(items.length) : "None"}
							</dd>
						</div>
						<div>
							<dt className="text-xs uppercase tracking-wide text-zinc-500">
								Last Sync
							</dt>
							<dd
								className="text-sm text-zinc-900"
								data-testid="awin-integration-api-key-publisher-id-last-sync"
							>
								{credsQ.isLoading
									? "loading"
									: lastSyncEpoch
										? formatTimestamp(lastSyncEpoch)
										: "never"}
							</dd>
						</div>
					</dl>
					{credsQ.isLoading ? (
						<p
							className="text-xs text-zinc-500"
							data-testid="awin-integration-api-key-publisher-id-loading"
							role="status"
						>
							Loading status.
						</p>
					) : null}
				</div>
			</section>

			{/* Connection form */}
			<form
				onSubmit={handleSubmit}
				className={`${CARD} flex flex-col gap-4`}
				data-testid="awin-integration-api-key-publisher-id-form"
				aria-label="partner network connection form"
			>
				<div className="flex flex-col gap-1">
					<h2 className="text-sm font-semibold text-zinc-800">
						Add Partner Network Credentials
					</h2>
					<p className="text-xs text-zinc-500">
						Your API key is hashed before storage. Only the last four
						characters are retained for display.
					</p>
				</div>

				<div className="flex flex-col gap-1.5">
					<label
						htmlFor="awin-integration-api-key-publisher-id-publisher-input"
						className={LABEL}
					>
						Publisher ID
					</label>
					<input
						id="awin-integration-api-key-publisher-id-publisher-input"
						type="text"
						required
						className={publisherIdValid ? INPUT : INPUT_INVALID}
						value={publisherId}
						autoComplete="off"
						onChange={(e) => setPublisherId(e.currentTarget.value)}
						data-testid="awin-integration-api-key-publisher-id-publisher-input"
						aria-invalid={!publisherIdValid}
					/>
					{!publisherIdValid ? (
						<p
							className="text-xs text-red-600"
							role="alert"
							data-testid="awin-integration-api-key-publisher-id-publisher-error"
						>
							Publisher ID must be alphanumeric (dash / underscore allowed).
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label
						htmlFor="awin-integration-api-key-publisher-id-key-input"
						className={LABEL}
					>
						API Key
					</label>
					<input
						id="awin-integration-api-key-publisher-id-key-input"
						type="password"
						required
						className={apiKeyValid ? INPUT : INPUT_INVALID}
						value={apiKey}
						autoComplete="off"
						onChange={(e) => setApiKey(e.currentTarget.value)}
						data-testid="awin-integration-api-key-publisher-id-key-input"
						aria-invalid={!apiKeyValid}
					/>
					{!apiKeyValid ? (
						<p
							className="text-xs text-red-600"
							role="alert"
							data-testid="awin-integration-api-key-publisher-id-key-error"
						>
							API key must be at least 16 characters, alphanumeric only.
						</p>
					) : (
						<p className="text-xs text-zinc-500">
							At least 16 characters. Alphanumeric, dash, underscore.
						</p>
					)}
				</div>

				<div className="flex flex-col gap-1.5">
					<label
						htmlFor="awin-integration-api-key-publisher-id-label-input"
						className={LABEL}
					>
						Label (optional)
					</label>
					<input
						id="awin-integration-api-key-publisher-id-label-input"
						type="text"
						className={INPUT}
						value={label}
						maxLength={200}
						autoComplete="off"
						onChange={(e) => setLabel(e.currentTarget.value)}
						data-testid="awin-integration-api-key-publisher-id-label-input"
						placeholder="Production account"
					/>
				</div>

				<div className="flex items-center gap-3">
					<button
						type="submit"
						className={BTN}
						disabled={!canSubmit}
						data-testid="awin-integration-api-key-publisher-id-save"
					>
						{createMut.isPending ? "Saving." : "Connect Partner Network"}
					</button>
					{saved ? (
						<p
							className="text-sm text-green-700"
							role="status"
							data-testid="awin-integration-api-key-publisher-id-saved"
						>
							Credentials saved.
						</p>
					) : null}
					{createMut.error ? (
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="awin-integration-api-key-publisher-id-error"
						>
							{createMut.error.message}
						</p>
					) : null}
				</div>
			</form>

			{/* Recent sync feed */}
			<section
				className={CARD}
				aria-label="recent sync feed"
				data-testid="awin-integration-api-key-publisher-id-feed"
			>
				<h2 className="text-sm font-semibold text-zinc-800">Recent Sync Activity</h2>
				{credsQ.isLoading ? (
					<p className="mt-3 text-sm text-zinc-500">Loading.</p>
				) : credsQ.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="awin-integration-api-key-publisher-id-feed-error"
					>
						{credsQ.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="awin-integration-api-key-publisher-id-empty"
					>
						No Partner Network credentials connected yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="awin-integration-api-key-publisher-id-list"
					>
						{items.map((row) => (
							<li
								key={row.id}
								className="flex items-center justify-between py-2 text-sm"
								data-testid={`awin-integration-api-key-publisher-id-row-${row.publisher_id}`}
							>
								<div className="flex flex-col gap-0.5">
									<span className="font-medium text-zinc-900">
										{row.publisher_id}
									</span>
									<span className="text-xs text-zinc-500">
										key ending in {row.api_key_last4 || "----"}
										{row.label ? ` - ${row.label}` : ""}
									</span>
									<span className="text-xs text-zinc-400">
										updated {formatTimestamp(row.updated_at || row.created_at)}
									</span>
								</div>
								<div className="flex items-center gap-2">
									<span
										className={
											row.status === "active"
												? "rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800"
												: "rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-700"
										}
									>
										{row.status}
									</span>
									{row.status === "active" ? (
										<button
											type="button"
											className={BTN_GHOST}
											disabled={deleteMut.isPending}
											onClick={() => deleteMut.mutate(row.publisher_id)}
											data-testid={`awin-integration-api-key-publisher-id-revoke-${row.publisher_id}`}
										>
											Revoke
										</button>
									) : null}
								</div>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AwinIntegrationApiKeyPublisherIdPage;
